import { yupResolver } from '@hookform/resolvers/yup';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import {
  CONTENT_FORMAT_DEFAULT,
  CONTENT_TYPE_DEFAULT,
  LANGUAGES_MULTIPLES_DEFAULT,
  PAGE_DEFAULT,
  QUERY_DEFAULT,
  SORT_ATTRIBUTE_CONTEXT_LIBRARY_DEFAULT,
  SORT_ORDER_DEFAULT
} from 'constants/defaults';
import {
  GeneratedContentFormatEnum,
  GeneratedContentPage,
  GeneratedContentSortAttributeEnum,
  GeneratedContentTypeEnum,
  InputMaybe,
  OrderableTargetLanguageCodesEnum,
  SortOrderEnum
} from 'gql/graphql';
import _ from 'lodash';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { useContentGenerationDataList } from 'providers/ContentGenerationProviders/useContentGenerationDataList';
import { useContentGenerationInfo } from 'providers/ContentGenerationProviders/useContentGenerationInfo';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { Option } from 'types/Option';
import { TGenericError } from 'types/TGenericError';
import { TScheme } from 'types/TScheme';
import { useDebounce } from 'use-debounce';
import { availableLanguageOptions } from 'utils/availableLanguageOptions';
import { getMostUsedTargetLanguagesUtil } from 'utils/getMostUsedTargetLanguagesUtil';
import { extractParam } from 'utils/paramsUtil';
import { contentFormatFactory } from './factories';

import * as yup from 'yup';

export interface FiltersContentGenerationForm {
  [KeysEnum.SortAttribute]: GeneratedContentSortAttributeEnum;
  [KeysEnum.SortOrder]: SortOrderEnum;
  [KeysEnum.Languages]?: InputMaybe<Array<OrderableTargetLanguageCodesEnum>>;
  [KeysEnum.Query]: string | undefined;
  [KeysEnum.Page]: number;
  [KeysEnum.Type]: InputMaybe<GeneratedContentTypeEnum>;
  [KeysEnum.ContentFormat]: InputMaybe<GeneratedContentFormatEnum>;
}

interface IUseSearchParamsContentGenerationOut {
  control: Control<FiltersContentGenerationForm>;
  deferredSearchQuery?: string;
  searchQuery?: string;
  data?: GeneratedContentPage;
  isLoading: boolean;
  isPending: boolean;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<GeneratedContentPage, TGenericError>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  optionsSortAttribute: Option[];
  optionsSortOrder: Option[];
  optionsLanguages: Option[];
  optionsContentType: Option[];
  optionsContentFormat: Option[];
  handleClear: () => void;
  isContentFormatFilterDisabled: boolean;
}

export default function useSearchParamsContentGeneration(): IUseSearchParamsContentGenerationOut {
  const locale = useLocale();
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { data: info } = useContentGenerationInfo();

  const [page, setPage] = useState(PAGE_DEFAULT);

  const pageInitial = extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT);
  const queryInitial = extractParam(searchParams, KeysEnum.Query, QUERY_DEFAULT);
  const sortAttributeInitial = extractParam(
    searchParams,
    KeysEnum.SortAttribute,
    SORT_ATTRIBUTE_CONTEXT_LIBRARY_DEFAULT
  );
  const sortOrderInitial = extractParam(searchParams, KeysEnum.SortOrder, SORT_ORDER_DEFAULT);
  const languagesInitial = extractParam(
    searchParams,
    KeysEnum.Languages,
    LANGUAGES_MULTIPLES_DEFAULT
  );
  const contentTypeInitial = extractParam(searchParams, KeysEnum.Type, CONTENT_TYPE_DEFAULT);
  const contentFormatInitial = extractParam(
    searchParams,
    KeysEnum.ContentFormat,
    CONTENT_FORMAT_DEFAULT
  );

  const isList = pathname.includes('/content-creator/list');

  const schema = yup.object().shape({} as TScheme<FiltersContentGenerationForm>);
  const {
    control,
    setValue,
    watch,
    getValues,
    reset,
    formState: { isDirty }
  } = useForm<FiltersContentGenerationForm>({
    resolver: yupResolver(schema)
  });

  const query = useWatch({
    control,
    name: KeysEnum.Query,
    defaultValue: queryInitial
  });

  const [deferredQuery] = useDebounce(query, 1000);

  const sortAttribute = useWatch<FiltersContentGenerationForm, typeof KeysEnum.SortAttribute>({
    control,
    name: KeysEnum.SortAttribute,
    defaultValue: watch(KeysEnum.SortAttribute) ?? sortAttributeInitial
  });

  const sortOrder = useWatch<FiltersContentGenerationForm, typeof KeysEnum.SortOrder>({
    control,
    name: KeysEnum.SortOrder,
    defaultValue: watch(KeysEnum.SortOrder) ?? sortOrderInitial
  });

  const languages = useWatch<FiltersContentGenerationForm, typeof KeysEnum.Languages>({
    control,
    name: KeysEnum.Languages,
    defaultValue: watch(KeysEnum.Languages) ?? languagesInitial
  });

  const contentType = useWatch<FiltersContentGenerationForm, typeof KeysEnum.Type>({
    control,
    name: KeysEnum.Type,
    defaultValue: watch(KeysEnum.Type) ?? contentTypeInitial
  });

  const contentFormat = useWatch<FiltersContentGenerationForm, typeof KeysEnum.ContentFormat>({
    control,
    name: KeysEnum.ContentFormat,
    defaultValue: watch(KeysEnum.ContentFormat) ?? contentFormatInitial
  });

  const {
    data: data,
    isLoading,
    isPending,
    refetch
  } = useContentGenerationDataList(
    {
      page,
      query: deferredQuery,
      sortAttribute,
      sortOrder,
      languageFilter: !!languages?.length ? languages : undefined,
      contentTypeFilter: contentType ? contentType : undefined,
      contentFormatFilter: contentFormat ? contentFormat : undefined
    },
    { enabled: deferredQuery === query }
  );

  const optionsSortAttribute = [
    {
      label: t(
        `content_generation.search.filters.sort_attributes.${GeneratedContentSortAttributeEnum.Title}`
      ),
      value: GeneratedContentSortAttributeEnum.Title
    },
    {
      label: t(
        `content_generation.search.filters.sort_attributes.${GeneratedContentSortAttributeEnum.Status}`
      ),
      value: GeneratedContentSortAttributeEnum.Status
    },
    {
      label: t(
        `content_generation.search.filters.sort_attributes.${GeneratedContentSortAttributeEnum.ContentType}`
      ),
      value: GeneratedContentSortAttributeEnum.ContentType
    },
    {
      label: t(
        `content_generation.search.filters.sort_attributes.${GeneratedContentSortAttributeEnum.ContentFormat}`
      ),
      value: GeneratedContentSortAttributeEnum.ContentFormat
    },
    {
      label: t(
        `content_generation.search.filters.sort_attributes.${GeneratedContentSortAttributeEnum.Languages}`
      ),
      value: GeneratedContentSortAttributeEnum.Languages
    },
    {
      label: t(
        `content_generation.search.filters.sort_attributes.${GeneratedContentSortAttributeEnum.Words}`
      ),
      value: GeneratedContentSortAttributeEnum.Words
    },
    {
      label: t(
        `content_generation.search.filters.sort_attributes.${GeneratedContentSortAttributeEnum.CreatedAt}`
      ),
      value: GeneratedContentSortAttributeEnum.CreatedAt
    }
  ];

  const optionsSortOrder = [
    {
      label: t(`content_generation.search.filters.sort_order.${SortOrderEnum.Asc}`),
      value: SortOrderEnum.Asc
    },
    {
      label: t(`content_generation.search.filters.sort_order.${SortOrderEnum.Desc}`),
      value: SortOrderEnum.Desc
    }
  ];

  const optionsContentType = [
    { label: t('content_generation.search.filters.all'), value: undefined },
    {
      label: t(`content_generation.content_types.${GeneratedContentTypeEnum.WrittenContent}`),
      value: GeneratedContentTypeEnum.WrittenContent
    },
    {
      label: t(`content_generation.content_types.${GeneratedContentTypeEnum.AudioContent}`),
      value: GeneratedContentTypeEnum.AudioContent
    },
    {
      label: t(`content_generation.content_types.${GeneratedContentTypeEnum.InteractiveContent}`),
      value: GeneratedContentTypeEnum.InteractiveContent
    },
    {
      label: t(`content_generation.content_types.${GeneratedContentTypeEnum.Presentations}`),
      value: GeneratedContentTypeEnum.Presentations
    }
  ];

  const optionsContentFormat = useMemo(() => {
    const options = [{ label: t('content_generation.search.filters.all'), value: undefined }];

    if (!contentType) return options;

    return [
      ...options,
      ...contentFormatFactory(contentType).map((format) => {
        return {
          label: t(`content_generation.content_formats.${format}`),
          value: format
        };
      })
    ];
  }, [contentType]);

  const optionsLanguages = [
    { label: t('content_generation.search.filters.all'), value: undefined },
    ...availableLanguageOptions(
      info?.targetLanguageCodes,
      [],
      getMostUsedTargetLanguagesUtil(info?.languagePairs ?? []),
      undefined,
      t,
      locale
    )
  ];
  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete(KeysEnum.Page);
      setPage(pageInitial);
      reset({
        ...getValues(),
        [KeysEnum.Type]: CONTENT_TYPE_DEFAULT as unknown as undefined,
        [KeysEnum.ContentFormat]: CONTENT_FORMAT_DEFAULT,
        [KeysEnum.Languages]: LANGUAGES_MULTIPLES_DEFAULT
      });
      window.history.replaceState({}, '', url.toString());
    }
  }, [setPage, setValue, reset, getValues]);

  useEffect(() => {
    setPage(pageInitial);
    if (queryInitial) setValue(KeysEnum.Query, queryInitial);
    if (sortAttributeInitial) setValue(KeysEnum.SortAttribute, sortAttributeInitial);
    if (sortOrderInitial) setValue(KeysEnum.SortOrder, sortOrderInitial);
    if (languagesInitial) setValue(KeysEnum.Languages, languagesInitial);
    if (contentTypeInitial) setValue(KeysEnum.Type, contentTypeInitial);
    if (contentFormatInitial) setValue(KeysEnum.Type, contentFormatInitial);
  }, [
    searchParams,
    pageInitial,
    queryInitial,
    sortAttributeInitial,
    sortOrderInitial,
    languagesInitial,
    contentTypeInitial,
    contentFormatInitial
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && isList) {
      const url = new URL(window.location.href);

      if (deferredQuery) url.searchParams.set(KeysEnum.Query, JSON.stringify(deferredQuery));
      else url.searchParams.delete(KeysEnum.Query);

      if (sortAttribute)
        url.searchParams.set(KeysEnum.SortAttribute, JSON.stringify(sortAttribute));
      else url.searchParams.delete(KeysEnum.SortAttribute);

      if (sortOrder) url.searchParams.set(KeysEnum.SortOrder, JSON.stringify(sortOrder));
      else url.searchParams.delete(KeysEnum.SortOrder);

      if (!!languages?.length) url.searchParams.set(KeysEnum.Languages, JSON.stringify(languages));
      else url.searchParams.delete(KeysEnum.Languages);

      if (contentType) url.searchParams.set(KeysEnum.Type, JSON.stringify(contentType));
      else url.searchParams.delete(KeysEnum.Type);

      if (contentFormat)
        url.searchParams.set(KeysEnum.ContentFormat, JSON.stringify(contentFormat));
      else url.searchParams.delete(KeysEnum.ContentFormat);

      url.searchParams.delete(KeysEnum.Page);
      setPage(
        [
          queryInitial !== deferredQuery,
          sortAttributeInitial !== sortAttribute,
          sortOrderInitial !== sortOrder,
          !_.isEqual(languagesInitial, languages),
          contentTypeInitial !== contentType,
          contentFormatInitial !== contentFormat
        ].some(Boolean) && isDirty
          ? PAGE_DEFAULT
          : pageInitial
      );

      window.history.replaceState({}, '', url.toString());
    }
  }, [
    isList,
    pageInitial,
    deferredQuery,
    sortAttribute,
    sortOrder,
    languages,
    queryInitial,
    contentType,
    contentFormat,
    sortAttributeInitial,
    sortOrderInitial,
    languagesInitial,
    contentTypeInitial,
    contentFormatInitial,
    isDirty
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && isList) {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname, isList]);

  const isContentFormatFilterDisabled = useMemo(
    () => contentType === contentTypeInitial,
    [contentType]
  );

  return {
    control,
    deferredSearchQuery: deferredQuery,
    searchQuery: query,
    data,
    isLoading,
    isPending,
    refetch,
    page,
    setPage,
    optionsSortAttribute,
    optionsSortOrder,
    optionsLanguages,
    optionsContentType,
    optionsContentFormat,
    handleClear,
    isContentFormatFilterDisabled
  };
}
